// @flow

import { useEffect, useRef, useCallback } from 'react';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

import type { Background } from './BackgroundSelector';

// WASM/model files are copied from the npm package into public/mediapipe/
// at build time (see public/mediapipe/). This avoids any external CDN dependency.
const MEDIAPIPE_BASE_PATH = `${process.env.PUBLIC_URL || ''}/mediapipe`;

// EMA weight applied to the previous frame's mask alpha.
// Higher = smoother but slower to track fast movement.
const EMA_DECAY = 0.7;

type Options = {
  width: number | null,
  height: number | null,
  background: Background,
  onReady: () => void,
  onError: () => void,
};

type Return = {
  videoRef: { current: HTMLVideoElement | null },
  canvasRef: { current: HTMLCanvasElement | null },
  streamRef: { current: MediaStream | null },
};

/**
 * Manages the camera stream, MediaPipe Selfie Segmentation, and per-frame
 * canvas compositing for the virtual background feature.
 *
 * Returns stable refs to the live <video> and output <canvas> elements so
 * the caller can attach them to DOM nodes and feed canvasRef into MediaRecorder.
 */
const useVideoCompositor = ({ width, height, background, onReady, onError }: Options): Return => {
  // DOM attachment refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Internal lifecycle refs — never trigger re-renders
  const streamRef = useRef<MediaStream | null>(null);
  const segRef = useRef<any>(null);
  const rafRef = useRef<AnimationFrameID | null>(null);
  const isRunningRef = useRef<boolean>(false);

  // Offscreen canvases: maskCanvas composites the person cutout; rawMaskCanvas
  // holds the raw segmentation mask for EMA alpha smoothing.
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rawMaskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const smoothedAlphaRef = useRef<Float32Array | null>(null);

  // Cache of pre-loaded HTMLImageElements keyed by URL so drawFrame never
  // stalls the render loop on a network/decode round-trip.
  const imageCacheRef = useRef<{ [url: string]: HTMLImageElement }>({});

  // Keep a ref in sync with the latest background so the RAF loop always
  // reads the current value without needing it as a reactive dependency.
  // Also pre-load image backgrounds as soon as they are selected.
  const backgroundRef = useRef<Background>(background);
  useEffect(() => {
    backgroundRef.current = background;
    if (background.type === 'image' && !imageCacheRef.current[background.url]) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = background.url;
      imageCacheRef.current[background.url] = img;
    }
  }, [background]);

  /**
   * MediaPipe onResults callback — composites the segmented person over the
   * selected virtual background on each frame.
   *
   * All values are read via refs so this function is stable across renders
   * and can be registered with seg.onResults once at initialisation time.
   */
  const drawFrame = useCallback((results: any) => {
    const canvas = canvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const rawMaskCanvas = rawMaskCanvasRef.current;
    if (!canvas || !maskCanvas || !rawMaskCanvas) return;

    const ctx = canvas.getContext('2d');
    const maskCtx = maskCanvas.getContext('2d');
    const bg = backgroundRef.current;
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    if (bg.type === 'none') {
      ctx.drawImage(results.image, 0, 0, w, h);
      return;
    }

    // --- Temporal EMA smoothing of the segmentation mask alpha channel ---
    // MediaPipe makes independent per-frame predictions, causing single-frame
    // dropout flicker on extremities (hands, arms). Blending the current mask
    // with the previous smoothed result suppresses this without visible lag.
    const rawMaskCtx = rawMaskCanvas.getContext('2d');
    rawMaskCtx.clearRect(0, 0, w, h);
    rawMaskCtx.drawImage(results.segmentationMask, 0, 0, w, h);
    const rawImageData = rawMaskCtx.getImageData(0, 0, w, h);
    const pixels = rawImageData.data;
    const pixelCount = w * h;

    if (!smoothedAlphaRef.current || smoothedAlphaRef.current.length !== pixelCount) {
      smoothedAlphaRef.current = new Float32Array(pixelCount);
    }
    const smoothed = smoothedAlphaRef.current;
    for (let i = 0; i < pixelCount; i += 1) {
      smoothed[i] = EMA_DECAY * smoothed[i] + (1 - EMA_DECAY) * (pixels[i * 4 + 3] / 255);
      pixels[i * 4 + 3] = smoothed[i] * 255;
    }
    rawMaskCtx.putImageData(rawImageData, 0, 0);
    // ---------------------------------------------------------------------

    // Step 1: draw the virtual background
    if (bg.type === 'blur') {
      const blurPx = bg.amount ?? 14;
      ctx.filter = `blur(${blurPx}px)`;
      // Oversized draw avoids darkened edges from the blur kernel
      ctx.drawImage(results.image, -24, -24, w + 48, h + 48);
      ctx.filter = 'none';
    } else if (bg.type === 'color') {
      ctx.fillStyle = bg.value;
      ctx.fillRect(0, 0, w, h);
    } else if (bg.type === 'image') {
      const img = imageCacheRef.current[bg.url];
      if (img && img.complete && img.naturalWidth > 0) {
        // Cover-fit: scale to fill the canvas, crop excess
        const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      } else {
        // Image still loading — neutral placeholder for this frame
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, w, h);
      }
    }

    // Step 2: composite the person cutout over the background.
    // For blur backgrounds the blurred video frame behind the person already
    // contains person-coloured pixels at the silhouette edge, which produces a
    // visible halo when the feathered mask gradient overlaps them. We prevent
    // this by eroding the mask inward (MASK_EROSION > blur radius) so the
    // entire fade zone lands on pure-person pixels.
    // For image/colour backgrounds the background contains no person colours, so
    // heavy erosion is unnecessary and actively worsens the cutout — a light
    // 1 px feather is enough for a natural edge.
    const maskErosion = bg.type === 'blur' ? 4 : 0;
    const maskBlurPx = bg.type === 'blur' ? 3 : 1;
    maskCtx.clearRect(0, 0, w, h);
    maskCtx.drawImage(results.image, 0, 0, w, h);
    maskCtx.globalCompositeOperation = 'destination-in';
    maskCtx.filter = `blur(${maskBlurPx}px)`;
    maskCtx.drawImage(rawMaskCanvas, maskErosion, maskErosion, w - maskErosion * 2, h - maskErosion * 2);
    maskCtx.filter = 'none';
    maskCtx.globalCompositeOperation = 'source-over';

    // Step 3: draw the composited person on top of the background
    ctx.drawImage(maskCanvas, 0, 0, w, h);
  }, []);

  /**
   * RAF-based render loop. Sends each video frame to MediaPipe when a virtual
   * background is active, or draws directly to the canvas when not needed.
   * Reads all mutable values via refs so it can be started once and remain
   * stable for the lifetime of the stream.
   */
  const startRenderLoop = useCallback(() => {
    const loop = async () => {
      if (!isRunningRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const seg = segRef.current;

      // readyState 2 = HAVE_CURRENT_DATA — frame data available
      if (video && canvas && video.readyState >= 2) {
        if (seg && backgroundRef.current.type !== 'none') {
          try {
            await seg.send({ image: video });
            // drawFrame is invoked via seg.onResults
          } catch (_) {
            // Segmentation error on this frame — fall back to direct draw
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          }
        } else {
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
      }

      rafRef.current = requestAnimationFrame(() => {
        loop();
      });
    };
    loop();
  }, []);

  // Main initialisation effect — runs once width and height are known.
  useEffect(() => {
    let cancelled = false;

    if (!width || !height) {
      return () => {
        cancelled = true;
      };
    }

    const init = async () => {
      try {
        const { mediaDevices } = navigator;
        if (!mediaDevices) throw new Error('getUserMedia not supported');
        const stream = await mediaDevices.getUserMedia({
          video: { width, height },
          audio: true,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        maskCanvasRef.current = maskCanvas;

        const rawMaskCanvas = document.createElement('canvas');
        rawMaskCanvas.width = width;
        rawMaskCanvas.height = height;
        rawMaskCanvasRef.current = rawMaskCanvas;
        smoothedAlphaRef.current = null;

        const seg = new SelfieSegmentation({
          locateFile: (file) => `${MEDIAPIPE_BASE_PATH}/${file}`,
        });

        seg.setOptions({
          modelSelection: 1, // landscape model — higher quality
          selfieMode: false, // mirroring applied via CSS scaleX(-1)
        });

        seg.onResults(drawFrame);
        await seg.initialize();

        if (cancelled) {
          seg.close();
          return;
        }

        segRef.current = seg;
        isRunningRef.current = true;
        onReady();
        startRenderLoop();
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[VideoBackRecorder] Initialization error:', err);
          onError();
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      isRunningRef.current = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (segRef.current) {
        segRef.current.close();
        segRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [width, height, drawFrame, startRenderLoop, onReady, onError]);

  return { videoRef, canvasRef, streamRef };
};

export default useVideoCompositor;
